/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ChangeDetectionStrategy} from '@angular/core';
import {EntityElementModel} from './models/entity-element.model';
import {EntityElementActionsEnum} from './models/entity-element-actions.enum';
import {ContextMenuActionEnum} from './models/context-menu-action.enum';
import {ContextMenuActionModel} from './models/context-menu-action.model';
import {ContextMenuTypeEnum} from './models/context-menu-type.enum';
import {ContextMenuModel} from '../../context-menu/context.menu.model';
import {EntityElementTypeEnum} from './models/entity-element-type.enum';
import {EntityElementContextMenuModel} from './entity-element-context-menu.model';
import {EntityTreeDisplayModeEnum} from '../../../settings/business-settings/models/entity-tree-display-mode.enum';
import {ContextMenuItemModel} from '../../context-menu/context-menu-item.model';
import {EntityElementWarningsModel} from './models/entity-element-warnings.model';
import {CloseContextMenuService} from '../../context-menu/services/close-context-menu.service';

@Component({
    selector: 'app-entity-element',
    templateUrl: './entity-element.component.html',
    styleUrls: ['./entity-element.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

export class EntityElementComponent implements OnInit, OnChanges {
    @Input() displayMode: EntityTreeDisplayModeEnum;
    @Input() element: EntityElementModel = {} as EntityElementModel;
    @Input() isSelected: boolean;
    @Input() contextMenuType: ContextMenuTypeEnum;
    @Input() entityElementType: EntityElementTypeEnum;
    @Input() isEnabled: boolean;
    @Input() showParentCode: boolean;
    @Input() warnings: EntityElementWarningsModel;
    @Output() contextMenuAction = new EventEmitter<ContextMenuActionModel>();
    @Output() removeAction = new EventEmitter<EntityElementModel>();
    public hasContextMenu: boolean;
    public elementActionButtonClass: string;
    private readonly CONTEXT_MENU_TOOLTIP = 'Backoffice actions';
    private contextMenus: EntityElementContextMenuModel;

    public get contextMenu(): ContextMenuModel {
        switch (this.contextMenuType) {
            case ContextMenuTypeEnum.CONNECTIONS_POOL:
                return this.contextMenus.connections_pool;
            case ContextMenuTypeEnum.PATH_CONNECTIONS_POOL:
                return this.contextMenus.path_connections_pool;
            case ContextMenuTypeEnum.PATH_POOL:
                return this.contextMenus.path_pool;
            case ContextMenuTypeEnum.EMPTY:
                return this.contextMenus.empty;
        }
    }

    public get isParentCodeVisible(): boolean {
        if (this.element != null) {
            return this.element.displayParentCode != null && this.showParentCode;
        }
        return false;
    }

    public get containerDataCy(): string {
        return `entity-element-${this.element.name}-container`;
    }

    public get baseDataCy(): string {
        return `entity-element-${this.element.code}-${this.entityElementType}`;
    }

    public get nameDataCy(): string {
        return `${this.baseDataCy}-name`;
    }

    public get codeDataCy(): string {
        return `${this.baseDataCy}-code`;
    }

    public get actionDataCy(): string {
        return `${this.baseDataCy}-action`;
    }

    public get parentCodeDataCy(): string {
        return `${this.baseDataCy}-parent-code`;
    }

    public get detailDataCy(): string {
        return `${this.baseDataCy}-detail`;
    }

    public get selected(): boolean {
        if (this.element != null) {
            return this.isSelected ? this.isSelected : this.element.isSelected;
        }
    }

    public get hasInactiveBorder(): boolean {
        if (this.element != null) {
            return !this.element.active
                    || (this.entityElementType !== EntityElementTypeEnum.POOL
                            && this.element.pathState != null
                            && this.element.pathState.inactivePath != null
                            && this.element.pathState.inactivePath.isInPath);
        }
    }

    public get hasUnfinishedPathMarker(): boolean {
        if (this.element != null) {
            return this.entityElementType === EntityElementTypeEnum.CONNECTION
                    && this.element.pathState != null
                    && this.element.pathState.unfinishedPath != null
                    && this.element.pathState.unfinishedPath.isOrigin;
        }
    }

    public get isConnectedWithParent(): boolean {
        if (this.element != null) {
            // If if has a parent code it means it is connected with a parent element
            return this.element.displayParentCode != null && this.element.connectedWithParent;
        }
    }

    public get isNewEntityElement(): boolean {
        if (this.element != null) {
            return this.element.isANewEntityElement;
        }
    }

    public get globalEntityElementClass(): string {
        return `entity-element`
                + `${this.displayMode != null ? ` ${this.displayMode}` : ''}`
                + `${this.elementIsInTreeClass != null ? ` ${this.elementIsInTreeClass}` : ''}`;
    }

    public get hasRemoveAction(): boolean {
        return this.displayMode === EntityTreeDisplayModeEnum.EDIT || this.entityElementType === EntityElementTypeEnum.PATH;
    }

    public get hasInlineWarnings(): boolean {
        return this.warnings != null && this.warnings.severity != null && this.warnings.labels != null;
    }

    private get elementIsInTreeClass(): string {
        return this.entityElementType !== EntityElementTypeEnum.POOL ? 'is-in-tree' : null;
    }

    constructor(private closeMenusService: CloseContextMenuService) {
    }

    closeContextMenus(): void {
        this.closeMenusService.closeAll();
    }

    ngOnInit(): void {
        this.manageActionIcon();

        this.contextMenus = this.createContextMenuModel();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['displayMode'] && !changes['displayMode'].firstChange
                && (changes['displayMode'].previousValue !== changes['displayMode'].currentValue)) {
            this.contextMenus = this.createContextMenuModel();
        }
    }

    contextMenuClick(action: ContextMenuActionEnum): void {
        this.contextMenuAction.emit({action: action, element: this.element});
    }

    removeClick(): void {
        this.removeAction.emit(this.element);
    }

    /**
     * Build the extra icon class name.
     * */
    public getIconClass(): string {
        let iconClass = 'entity-element-icon';
        if (this.element != null) {

            if (this.element.iconCode != null) {
                iconClass += ` ${this.element.iconCode}`;

                if (this.element.isSelected) {
                    iconClass += ' selected-element-icon';
                }
            }

        }
        return iconClass;
    }

    /**
     * Check if this entity contains icon.
     * */
    public hasIcon(): boolean {
        return this.element != null && this.element.iconCode != null;
    }

    public hasDisplayCode(): boolean {
        return this.element.hasDisplayCode;
    }

    /**
     * Manages the action icon (⁝ or ×)
     */
    private manageActionIcon(): void {
        this.elementActionButtonClass = 'entity-action-button';
        this.hasContextMenu = false;
        if (this.element != null) {
            switch (this.element.action) {
                case EntityElementActionsEnum.DELETE:
                    this.elementActionButtonClass += ' iwp-icon-gen_close_s';
                    break;
                case EntityElementActionsEnum.EDIT:
                    this.hasContextMenu = true;
                    break;
                default:
                    break;
            }
        }
    }

    // -------------------- Context menu helper functions --------------------

    private createPutInPathContextMenuItemModel(): ContextMenuItemModel {
        let disabled = true;

        if (this.element.contextMenuActions != null) {
            disabled = !this.element.contextMenuActions.includes(ContextMenuActionEnum.PUT_IN_PATH)
                    || this.displayMode === EntityTreeDisplayModeEnum.VIEW;
        }

        return {
            label: ContextMenuActionEnum.PUT_IN_PATH,
            action: (): void => this.contextMenuClick(ContextMenuActionEnum.PUT_IN_PATH),
            disabled
        };
    }

    private createConnectContextMenuItemModel(): ContextMenuItemModel {
        let disabled = true;

        if (this.element != null && this.element.contextMenuActions != null) {
            disabled = !this.element.contextMenuActions.includes(ContextMenuActionEnum.CONNECT)
                    || this.displayMode === EntityTreeDisplayModeEnum.VIEW;
        }

        return {
            label: ContextMenuActionEnum.CONNECT,
            action: (): void => this.contextMenuClick(ContextMenuActionEnum.CONNECT),
            disabled
        };
    }

    private createEditContextMenuItemModel(): ContextMenuItemModel {
        let disabled = true;

        if (this.element != null && this.element.contextMenuActions != null) {
            disabled = !this.element.contextMenuActions.includes(ContextMenuActionEnum.EDIT)
                    || this.displayMode === EntityTreeDisplayModeEnum.VIEW;
        }

        return {
            label: ContextMenuActionEnum.EDIT,
            action: (): void => this.contextMenuClick(ContextMenuActionEnum.EDIT),
            disabled
        };
    }

    private createDeleteContextMenuItemModel(): ContextMenuItemModel {
        let disabled = true;

        if (this.element != null && this.element.contextMenuActions != null) {
            disabled = !this.element.contextMenuActions.includes(ContextMenuActionEnum.DELETE)
                    || this.displayMode === EntityTreeDisplayModeEnum.VIEW;
        }

        return {
            label: ContextMenuActionEnum.DELETE,
            action: (): void => this.contextMenuClick(ContextMenuActionEnum.DELETE),
            disabled,
            hasTopSeparator: true
        };
    }

    private createContextMenuModel(): EntityElementContextMenuModel {
        return {
            path_pool: {
                code: this.element.code,
                tooltip: this.CONTEXT_MENU_TOOLTIP,
                items: [
                    this.createPutInPathContextMenuItemModel(),
                    this.createEditContextMenuItemModel(),
                    this.createDeleteContextMenuItemModel()
                ]
            },
            connections_pool: {
                code: this.element.code,
                tooltip: this.CONTEXT_MENU_TOOLTIP,
                items: [
                    this.createConnectContextMenuItemModel(),
                    this.createEditContextMenuItemModel(),
                    this.createDeleteContextMenuItemModel()
                ]
            },
            path_connections_pool: {
                code: this.element.code,
                tooltip: this.CONTEXT_MENU_TOOLTIP,
                items: [
                    this.createPutInPathContextMenuItemModel(),
                    this.createConnectContextMenuItemModel(),
                    this.createEditContextMenuItemModel(),
                    this.createDeleteContextMenuItemModel()
                ]
            },
            empty: {
                code: this.element.code,
                tooltip: this.CONTEXT_MENU_TOOLTIP,
                items: [
                    this.createEditContextMenuItemModel(),
                    this.createDeleteContextMenuItemModel()
                ]
            }
        };
    }
}
